import { LangTranslate } from "../../types/types";
import { api } from "./api";
import { UpdateTranslationsRequest } from "../types/translate.types";

// Type definition for bulk assign exercises request
interface BulkAssignExercisesRequest {
  exerciseIds: number[];
  userIds: number[];
  companyId: number;
}

// Type definition for bulk unassign exercises request
interface BulkUnassignExercisesRequest {
  exerciseIds: number[];
  userIds: number[];
  companyId: number;
}

// Type definition for bulk assign exercises response
interface BulkAssignExercisesResponse {
  success: boolean;
  message: string;
  assignmentsAdded: number;
  totalPossibleAssignments: number;
  skippedExistingAssignments: number;
}

// Type definition for bulk unassign exercises response
interface BulkUnassignExercisesResponse {
  success: boolean;
  message: string;
  assignmentsRemoved: number;
  totalPossibleRemovals: number;
  skippedNonExistingAssignments: number;
}

// Type definition for users assigned to exercise response
interface AssignedUser {
  userId: number;
  fullName: string;
}

interface GetUsersAssignedToExerciseResponse {
  assignedUsers: AssignedUser[];
  totalUsers: number;
}

export const adminApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTranslation: build.query<LangTranslate[], string>({
      query: (text) => ({
        url: `/AI/AI_Translate`,
        method: "POST",
        body: { Text: text },
      }),
    }),

    saveTranslation: build.mutation<boolean, UpdateTranslationsRequest>({
      query: (request) => ({
        url: `/Languages/Update`,
        method: "POST",
        body: request,
      }),
    }),

    bulkAssignExercises: build.mutation<BulkAssignExercisesResponse, BulkAssignExercisesRequest>({
      query: (request) => ({
        url: `/admintech/BulkAssignExercises/`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["user", "ExerciseAssignments"], // Invalidate user cache since exercise assignments changed
    }),

    bulkUnassignExercises: build.mutation<BulkUnassignExercisesResponse, BulkUnassignExercisesRequest>({
      query: (request) => ({
        url: `/admintech/BulkUnassignExercises/`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["user", "ExerciseAssignments"], // Invalidate user cache since exercise assignments changed
    }),

    getUsersAssignedToExercise: build.query<GetUsersAssignedToExerciseResponse, number>({
      query: (exerciseId) => ({
        url: `/Admintech/GetUsersAssignedToExercise/${exerciseId}`,
        method: "GET",
      }),
      providesTags: (result, error, exerciseId) => [
        { type: "ExerciseAssignments", id: exerciseId },
      ],
    }),
  }),
});

export const {
  useLazyGetTranslationQuery,
  useSaveTranslationMutation,
  useBulkAssignExercisesMutation,
  useBulkUnassignExercisesMutation,
  useGetUsersAssignedToExerciseQuery,
  useLazyGetUsersAssignedToExerciseQuery,
} = adminApi;
